import {
  type InfiniteScrollCustomEvent,
  IonContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSkeletonText,
  type RefresherEventDetail,
} from '@ionic/react';
import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Redirect } from 'react-router-dom';

import type { Company } from '~/api/auth';
import type { CompanyDocument, FeedPost } from '~/api/documents';
import { useCompany } from '~/api/hooks/useCompany';
import { type FeedPages, useFeedInfiniteQuery } from '~/api/hooks/useDocuments';
import { CompanySwitcher } from '~/components/CompanySwitcher';
import { useTenant } from '~/components/TenantGate';
import { Icon } from '~/components/ui/Icon';
import { useAppNavigation } from '~/hooks/useAppNavigation';
import { DocumentoSheet } from '~/pages/documentos/DocumentoSheet';
import { PostCard, officeShortName } from '~/pages/documentos/PostCard';
import { useLastVisit } from '~/pages/documentos/useLastVisit';
import { ROUTES } from '~/routes';
import { isNewSince } from '~/session/documentVisits';

type FeedQuery = UseInfiniteQueryResult<FeedPages, Error>;

const SKELETON_POSTS = [0, 1, 2];

const FOLDERS_LINK_CLASS =
  'inline-flex items-center gap-1.5 rounded-[10px] border-hair border-rule bg-bg-surface px-3 py-2 text-[13px] font-medium text-ink no-underline';

function postsOf(feed: FeedQuery): FeedPost[] {
  return feed.data?.pages.flatMap((page) => page.results) ?? [];
}

async function loadMorePosts(feed: FeedQuery, event: InfiniteScrollCustomEvent): Promise<void> {
  if (feed.hasNextPage && !feed.isFetchingNextPage) {
    await feed.fetchNextPage();
  }
  await event.target.complete();
}

async function refreshFeed(
  feed: FeedQuery,
  event: CustomEvent<RefresherEventDetail>,
): Promise<void> {
  await feed.refetch();
  event.detail.complete();
}

/**
 * `/documentos`: o que o escritório publicou para a empresa em operação, do mais novo ao mais
 * antigo, com "Novo" no que chegou desde a última visita; tocar num documento o abre no
 * visualizador. Fica atrás de `RequireModule` (`documents` `READ`), e a consulta também não sai
 * abaixo desse nível. O toque no push de um post abre `?empresa=<id>`, e o `CompanyFromUrl` do
 * `App.tsx` põe essa empresa em operação antes do guarda.
 */
export function Feed(): JSX.Element {
  const { selected } = useCompany();

  return (
    <IonPage>
      <IonContent fullscreen>
        {selected ? <CompanyFeed company={selected} /> : <Redirect to={ROUTES.dashboard} />}
      </IonContent>
    </IonPage>
  );
}

function CompanyFeed({ company }: { company: Company }): JSX.Element {
  const nav = useAppNavigation();
  const tenant = useTenant();
  const feed = useFeedInfiniteQuery(company.id);
  const seenAt = useLastVisit(company.id);
  const [openedDocument, setOpenedDocument] = useState<CompanyDocument | null>(null);
  const posts = postsOf(feed);

  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={(event) => refreshFeed(feed, event)}>
        <IonRefresherContent />
      </IonRefresher>

      <div className="rx-container rx-screen pb-7">
        <div className="px-6 pt-3">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              className="rx-iconbtn"
              onClick={() => nav.replace(ROUTES.dashboard)}
              aria-label="Voltar"
            >
              <Icon name="back" size={18} />
            </button>
            <Link to={ROUTES.documentosPastas} className={FOLDERS_LINK_CLASS}>
              <span aria-hidden="true">
                <Icon name="folder" size={16} />
              </span>
              Pastas
            </Link>
          </div>
          <h1 className="mb-0 mt-3.5 text-[30px] font-medium tracking-display-tight text-ink">
            Documentos
          </h1>
          <div className="mt-2">
            <CompanySwitcher compact />
          </div>
        </div>

        <div className="flex flex-col gap-3 px-6 pt-5">
          {feed.isPending ? (
            <FeedSkeleton />
          ) : posts.length === 0 ? (
            <EmptyFeed officeName={officeShortName(tenant)} />
          ) : (
            posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                tenant={tenant}
                isNew={isNewSince(post.created_at, seenAt)}
                openedDocumentId={openedDocument?.id ?? null}
                onOpenDocument={setOpenedDocument}
              />
            ))
          )}
        </div>

        <IonInfiniteScroll
          onIonInfinite={(event) => loadMorePosts(feed, event)}
          disabled={!feed.hasNextPage}
          threshold="200px"
        >
          <IonInfiniteScrollContent
            loadingSpinner="dots"
            loadingText="Carregando mais documentos..."
          />
        </IonInfiniteScroll>
      </div>

      <DocumentoSheet
        companyDocument={openedDocument}
        clientId={company.id}
        onClose={() => setOpenedDocument(null)}
      />
    </>
  );
}

function FeedSkeleton(): JSX.Element {
  return (
    <output aria-label="Carregando os documentos" className="flex flex-col gap-3">
      {SKELETON_POSTS.map((index) => (
        <div key={index} className="rx-card">
          <IonSkeletonText animated style={{ width: '40%', height: 12 }} />
          <IonSkeletonText animated style={{ width: '70%', height: 15, marginTop: 12 }} />
          <IonSkeletonText animated style={{ width: '90%', height: 13, marginTop: 10 }} />
        </div>
      ))}
    </output>
  );
}

function EmptyFeed({ officeName }: { officeName: string }): JSX.Element {
  return (
    <div className="rx-card text-center" style={{ padding: '28px 16px' }}>
      <span
        aria-hidden="true"
        className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-full bg-brand-tint text-brand"
      >
        <Icon name="folder" size={22} />
      </span>
      <p className="mb-0 mt-3.5 text-overline font-semibold uppercase text-ink-subtle">
        Nenhum documento ainda
      </p>
      <p className="mx-auto mb-0 mt-2 max-w-[32ch] text-[13px] text-ink-muted">
        Quando {officeName} publicar um documento desta empresa, ele aparece aqui.
      </p>
    </div>
  );
}
